import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { visibleStars } from './roundGenerator.mjs';
import { sunAltitude } from './astronomy.mjs';
import { makeCurve } from './brightness.mjs';
import { horizontalFovDeg } from './viewport.mjs';
import { useSettings } from './SettingsMenu.jsx';
import { StarCard } from './SkyViewer.jsx';
import {
  altAzToVector,
  extinctionFactor,
  makeCamera,
  projectVector,
  starColor
} from './skyRender.mjs';

/*
 * The landing page's background: the real sky, right now, from somewhere it is
 * genuinely dark.
 *
 * This is decoration, but it is not a mock-up. The same rule the tutorial is
 * held to applies here — the quest's whole claim is that its sky is computed
 * rather than drawn, and a landing page scattering random dots behind that claim
 * would be the one place on the site telling a small lie. It costs almost
 * nothing to be honest: `visibleStars` and the projection are already loaded for
 * the game, so this is a few dozen lines on top of code that is already there.
 *
 * What it is *not* is a second sky viewer. No constellations, no labels, no
 * Moon, no Milky Way, no interaction. Those all belong to the round, and putting
 * them here would leave the type competing with a playable sky for attention.
 */

// Where to stand. The anti-solar meridian is the deepest night on Earth at any
// instant, so this is always dark and always somewhere different.
const BACKDROP_LAT = 35;

/*
 * A wide, low camera. Wide because a full field of stars reads as texture where
 * a narrow one reads as a picture of a constellation, and low so the drift runs
 * along the frame rather than wheeling around a pole in the corner.
 *
 * The field is stated vertically and the horizontal one is derived per frame
 * from the window's shape — see `horizontalFovDeg`, which explains why. Eighty
 * degrees centred 38° up runs the frame from a couple of degrees under the
 * horizon to a little short of the zenith, which is the composition this was
 * always drawing on a desktop window; saying it in these terms is what makes a
 * phone get it too.
 */
const VFOV_DEG = 80;
const ALT_DEG = 38;

// A degree every eight seconds: a full turn takes about three quarters of an
// hour. It should read as "did that move?", not as a screensaver.
const DEG_PER_MS = 1 / 8000;

/*
 * Faint stars are the texture and there are thousands of them, but past a point
 * they are single dim pixels costing a fill each. Six is already fainter than a
 * dark-sky naked eye gets.
 */
const FAINTEST = 6.0;

/*
 * Deliberate gain over the game's calibration, which is tuned for a sky you are
 * actively reading at full screen: there, the median star is a 0.6px dot at 0.2
 * alpha, and it works because you are staring at it. Behind a page of type the
 * same numbers are simply invisible — the first attempt at this rendered 2,433
 * real stars and looked like an empty black rectangle. So the field is drawn
 * heavier than the truth, which is the one honest trade here: the star
 * *positions* are still real, only their weight is nudged to where a background
 * can carry them.
 */
const RADIUS_GAIN = 1.9;
const ALPHA_GAIN = 1.55;

function darkestLongitude(date) {
  let best = 0;
  let lowest = Infinity;
  for (let lon = -180; lon < 180; lon += 5) {
    const alt = sunAltitude(BACKDROP_LAT, lon, date);
    if (alt < lowest) {
      lowest = alt;
      best = lon;
    }
  }
  return best;
}

/*
 * Where a hovercard must not appear: over anything that paints. The canvas is
 * `pointer-events: none` and covers the whole viewport, so the honest way to ask
 * "is there a star visible under the cursor, or is something on top of it" is to
 * ask the document what is actually there. These are the elements that draw
 * something rather than the containers around them — blocking a container would
 * kill hover across all the empty space inside it.
 */
const OVER_CONTENT = [
  '.sg-header',
  '.sg-tier-main',
  '.sg-tier-played',
  '.sg-learn',
  '.sg-start-foot',
  '.sg-home',
  '.sg-gear',
  '.sg-how-veil',
  '.sg-summary',
  '.sg-board'
].join(', ');

export default function SkyBackdrop({ catalog }) {
  const canvasRef = useRef(null);
  const settings = useSettings();
  // Screen positions from the last painted frame, in CSS pixels, for hit-testing.
  const screenStars = useRef([]);
  const [hover, setHover] = useState(null);
  /*
   * How far the drift has turned, kept across effect restarts. Changing the
   * brightness rebuilds the star list and so re-runs the draw effect, and
   * without this the sky would snap back to its opening bearing every time —
   * which is exactly when someone is watching it, since the settings panel opens
   * over the start screen.
   */
  const phaseRef = useRef(0);

  /*
   * Where the stars are. Fixed for the life of the page — the observer and the
   * instant are chosen once — so this survives a settings change untouched, and
   * flipping the brightness does not re-run the astronomy for 2,400 stars.
   */
  const sky = useMemo(() => {
    if (!catalog) return [];
    const now = new Date();
    const lon = darkestLongitude(now);
    return visibleStars(catalog, now, BACKDROP_LAT, lon)
      .filter((star) => star.mag <= FAINTEST)
      .map((star) => ({
        vec: altAzToVector(star.alt, star.az),
        mag: star.mag,
        extinction: extinctionFactor(star.alt),
        color: starColor(star.spectralClass),
        // Carried through for the hovercard, which is the same component the
        // round uses and wants the same fields.
        star
      }));
  }, [catalog]);

  /*
   * How heavily to draw them, which is the half that the brightness setting
   * moves. The backdrop honours it for the same reason the round does: someone
   * who has turned the sky down because it is too busy meant the whole game, not
   * just the part with a guess attached.
   */
  const painted = useMemo(() => {
    const curve = makeCurve(settings.brightness);
    return sky.map((star) => {
      const brightness = curve.brightness(star.mag) * star.extinction;
      return {
        vec: star.vec,
        color: star.color,
        star: star.star,
        radius: curve.radius(brightness) * RADIUS_GAIN,
        alpha: Math.min(1, curve.alpha(brightness) * ALPHA_GAIN)
      };
    });
  }, [sky, settings.brightness]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !painted.length) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    let dpr = 1;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
    };

    const draw = (azDeg) => {
      const camera = makeCamera({
        azDeg,
        altDeg: ALT_DEG,
        width,
        height,
        fovDeg: horizontalFovDeg(width, height, VFOV_DEG)
      });
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      const onScreen = [];
      for (const star of painted) {
        const at = projectVector(star.vec, camera);
        if (!at || at.x < -4 || at.x > width + 4 || at.y < -4 || at.y > height + 4) continue;
        // Only stars actually drawn are hoverable, and only ones worth naming.
        if (star.star.name || star.star.bayer) onScreen.push({ x: at.x, y: at.y, star: star.star });

        const { radius, alpha } = star;
        const [r, g, b] = star.color;

        // A halo on the few that earn one. Without it every star is the same
        // dot and the field reads as noise rather than as a sky.
        if (radius > 2.2) {
          // Tight. At four radii the brightest few stopped reading as stars and
          // started reading as smudges on the lens.
          const halo = radius * 2.4;
          const glow = ctx.createRadialGradient(at.x, at.y, 0, at.x, at.y, halo);
          glow.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${alpha * 0.22})`);
          glow.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(at.x, at.y, halo, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
        ctx.beginPath();
        ctx.arc(at.x, at.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      screenStars.current = onScreen;
    };

    resize();

    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (still) {
      draw(180 + phaseRef.current);
      const onResize = () => {
        resize();
        draw(180 + phaseRef.current);
      };
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }

    let frame = 0;
    let start = null;
    let advanced = phaseRef.current;
    const tick = (time) => {
      if (start === null) start = time;
      advanced = phaseRef.current + (time - start) * DEG_PER_MS;
      draw(180 + advanced);
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);

    const onResize = () => resize();
    window.addEventListener('resize', onResize);
    return () => {
      phaseRef.current = advanced;
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
    };
  }, [painted]);

  /*
   * Hover, from a canvas that deliberately cannot receive pointer events.
   *
   * The canvas covers the viewport and must stay click-through, so the move is
   * listened for on the window and the document is asked what is actually under
   * the cursor. That answers the "except where the stars are behind something"
   * part properly: if anything that paints is on top, there is nothing to point
   * at, whatever the sky is doing behind it.
   */
  const onMove = useCallback((event) => {
    const under = document.elementFromPoint(event.clientX, event.clientY);
    if (!under || under.closest(OVER_CONTENT)) {
      setHover(null);
      return;
    }

    // The same generous reach the round uses: a star is a couple of pixels
    // across, and asking anyone to land a cursor on that exactly is no offer.
    let best = null;
    let bestDistance = 14 * 14;
    for (const candidate of screenStars.current) {
      const d = (candidate.x - event.clientX) ** 2 + (candidate.y - event.clientY) ** 2;
      if (d < bestDistance) {
        bestDistance = d;
        best = candidate;
      }
    }
    if (!best) {
      setHover(null);
      return;
    }

    /*
     * Kept inside the viewport. The card is up to 250px wide and sits to the
     * right of its star, and the start screen is pinned with nowhere to scroll,
     * so a card opening off the right edge would either be clipped or push a
     * scrollbar onto a page whose whole point is not having one. The lower bound
     * matters on a narrow window, where `innerWidth - 275` goes negative and the
     * clamp that was meant to keep the card on screen walked it off the left.
     */
    setHover({
      star: best.star,
      x: Math.max(8, Math.min(best.x, window.innerWidth - 275)),
      y: Math.min(Math.max(best.y, 70), window.innerHeight - 70)
    });
  }, []);

  /*
   * Only where there is a pointer that can hover.
   *
   * A touch screen reports `pointermove` while a finger is down, so on a phone
   * this listener turns a drag across the page into a card that appears under
   * the finger and then stays there, over the two buttons the screen exists for,
   * until something else is tapped. That is a worse offer than not having it:
   * the feature is "point at a star and it tells you what it is", and pointing
   * is the part a touch screen does not do.
   */
  useEffect(() => {
    const canHover = window.matchMedia('(hover: hover)');
    const clear = () => setHover(null);

    const bind = () => {
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerdown', clear);
      document.addEventListener('pointerleave', clear);
    };
    const unbind = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', clear);
      document.removeEventListener('pointerleave', clear);
      clear();
    };

    // Re-checked rather than read once: plugging a mouse into a tablet flips it.
    const sync = () => (canHover.matches ? bind() : unbind());
    sync();
    canHover.addEventListener('change', sync);
    return () => {
      canHover.removeEventListener('change', sync);
      unbind();
    };
  }, [onMove]);

  return (
    <>
      <canvas className="sg-backdrop" ref={canvasRef} aria-hidden="true" />
      {hover && (
        <div className="sg-backdrop-hover">
          <StarCard star={hover.star} x={hover.x} y={hover.y} />
        </div>
      )}
    </>
  );
}
